import React from 'react';
import './seat-map.scss';
import classNames from 'classnames';
import SEAT_MAPS from './seats';
import getTagValue from '../../../utilities/get-tag-value';
import {TransformComponent, TransformWrapper, useControls} from 'react-zoom-pan-pinch';
import useWindowDimensions from '../../../useWindowDimentions';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import {FetchKeyStateContext} from '../../../components/FetchKey';
import SectorSeatMap, {SectorMap, SectorData} from './sector-map';
import LogoLoadingIcon from '../../../assets/logo-loading.svg';
import PlusIcon from '../../../assets/icons0/plus.svg';
import MinusIcon from '../../../assets/icons0/minus.svg';
import MarkIcon from '../../../assets/icons0/mark.svg';
import CornerUpLeftIcon from '../../../assets/icons0/corner-up-left.svg';

export const MapActions = React.memo(({showInitialMap, initialScale}) => {
    const { zoomIn, zoomOut, centerView } = useControls();
    return (
        <div className='position-absolute left-0 top-0 display-flex flex-direction-column row-gap-0dot5rem z-index-1'>
            <div className='display-flex flex-direction-column seat-map__button-group'>
                <button className='seat-map__button cursor-pointer border-1px-solid border-color-gray-300 background-color-white border-radius-0dot5rem padding-0dot25rem' onClick={() => zoomIn()}>
                    <PlusIcon className='display-block width-1dot25rem height-1dot25rem color-gray-500'/>
                </button>
                <button className='seat-map__button cursor-pointer border-1px-solid border-color-gray-300 background-color-white border-radius-0dot5rem padding-0dot25rem' onClick={() => zoomOut()}>
                    <MinusIcon className='display-block width-1dot25rem height-1dot25rem color-gray-500'/>
                </button>
            </div>
            <button className='seat-map__button cursor-pointer border-1px-solid border-color-gray-300 background-color-white border-radius-0dot5rem padding-0dot25rem' onClick={() => {centerView(initialScale)}}>
                <MarkIcon className='display-block width-1dot25rem height-1dot25rem color-gray-500'/>
            </button>
                <button className={classNames('seat-map__button cursor-pointer border-1px-solid border-color-gray-300 background-color-white border-radius-0dot5rem padding-0dot25rem', {'seat-map__button--hide': !showInitialMap})} onClick={() => showInitialMap()}>
                    <CornerUpLeftIcon className='display-block width-1dot25rem height-1dot25rem color-gray-500'/>
                </button>
        </div>
    )
});

export const Seat = React.memo(({endpoint, priceCategories, localCartState, x, y, rx, transform, width, height, svgTag = 'rect', d, allowMultipleBuy, sectorName, row, seat, mirrorSeatNumbers}) => {
    const colorClass = (!endpoint || endpoint.quantity === 0) ? `${svgTag === 'rect' ? 'seat' : svgTag}--disabled` :  `${svgTag === 'rect' ? 'seat' : svgTag}--color-${priceCategories.indexOf(endpoint.price)}`;
    const xTextVal = mirrorSeatNumbers ? parseFloat(x) - 8.5 : parseFloat(x) + 9.5; // 9.5 - half of width of seat rect
    return (
        <>
            {!!endpoint && 
                <>
                    {svgTag === 'rect' && 
                        <>
                            <rect {...{x, y, rx, transform, width, height}} data-sector-name={sectorName} data-coords={`${row},${seat}`} data-id={endpoint.id} data-multiple={allowMultipleBuy} className={classNames(`seat ${colorClass}`, {'seat--selected': !!localCartState[endpoint.id]})}/>
                            <text {...{x: xTextVal, y: parseFloat(y) + 13.5, rx, transform: `${transform || ''}${mirrorSeatNumbers ? ' scale(-1, 1)' : ''}`}} fill="white" textAnchor="middle" alignmentBaseline="central" pointerEvents='none' data-legend={`${row},${seat}`}>{seat}</text>
                        </>
                    }
                    {svgTag === 'path' && <path {...{d}} data-id={endpoint.id} data-sector-name={sectorName} data-coords={`${row},${seat}`} data-multiple={allowMultipleBuy} className={classNames(`path ${colorClass}`, {'path--selected': !!localCartState[endpoint.id]})}/>}
                </>
            }
            {!endpoint &&
                <>
                    {svgTag === 'rect' &&
                        <>
                            <rect {...{x, y, rx, transform, width, height}} className='seat seat--disabled' />
                            <text {...{x: xTextVal, y: parseFloat(y) + 13.5, rx, transform: `${transform || ''}${mirrorSeatNumbers ? ' scale(-1, 1)' : ''}`}} fill="#344054" textAnchor="middle" alignmentBaseline="central" pointerEvents='none' data-legend={`${row},${seat}`}>{seat}</text>
                        </>
                    }
                    {svgTag === 'path' && <path {...{d}} data-sector-name={sectorName} data-coords={`${row},${seat}`} className={`path path--disabled`}/>}
                </>
            }
        </>
    )
});

const ClickableSector = React.memo(({x, y, rx, transform, width, height, svgTag = 'rect', d, classes, name, serviceId}) => {
    const fetchKeyState = React.useContext(FetchKeyStateContext);
    const {viewer} = useLazyLoadQuery(
        graphql`
            query seatMapPalacSportuFancyPagesViewerClickableSectorQuery($first: Int!, $after: String, $tagsSection: String, $serviceId: String, $available: Boolean) {
                viewer {
                    ...seatMapPalacSportuFancyPagesClickableSectorViewer_viewer
                }
            }
        `,
        {first: 1, serviceId, tagsSection: name, available: true},
        {fetchKey: fetchKeyState}
    );
    const {data} = usePaginationFragment(
        graphql`
            fragment seatMapPalacSportuFancyPagesClickableSectorViewer_viewer on Viewer @refetchable(queryName: "seatMapPalacSportuFancyPagesClickableSectorSearchEndpointsQuery") {
                searchEndpoints(first: $first, after: $after, tagsSection: $tagsSection, serviceId: $serviceId, available: $available) @connection(key: "seatMapPalacSportuClickableSector_searchEndpoints") {
                    edges {
                        node {
                            id
                        }
                    }
                }
            }
        `,
        viewer
    );
    const disabled = data.searchEndpoints.edges.length === 0;
    return (
        <>
            {svgTag === 'rect' && <rect {...{x, y, rx, transform, width, height}} data-sector={name} className={`${classes}${disabled ? ' seat--disabled' : ''}`}/>}
            {svgTag === 'path' && <path {...{d}} data-sector={name} className={`${classes}${disabled ? ' path--disabled' : ''}`}/>}
        </>
    )
});

const SectorEndpoints = ({sectorName, serviceId, sectorData, addPriceCategory, priceCategories, children}) => {
    const fetchKeyState = React.useContext(FetchKeyStateContext);
    const {viewer} = useLazyLoadQuery(
        graphql`
            query seatMapPalacSportuFancyPagesViewerQuery($first: Int!, $after: String, $tagsSection: String, $serviceId: String, $available: Boolean) {
                viewer {
                    ...seatMapPalacSportuFancyPagesViewer_viewer
                }
            }
        `,
        {first: 100500, serviceId, tagsSection: sectorName, available: true},
        {fetchKey: fetchKeyState}
    );
    const {data, loadNext, hasNext, isLoadingNext} = usePaginationFragment(
        graphql`
            fragment seatMapPalacSportuFancyPagesViewer_viewer on Viewer @refetchable(queryName: "seatMapPalacSportuFancyPagesSearchEndpointsQuery") {
                searchEndpoints(first: $first, after: $after, tagsSection: $tagsSection, serviceId: $serviceId, available: $available) @connection(key: "seatMapPalacSportu_searchEndpoints") {
                    edges {
                        node {
                            id
                            labels
                            quantity
                            price
                        }
                    }
                }
            }
        `,
        viewer
    );
    React.useEffect(() => {
        data.searchEndpoints.edges.map(e => e.node).forEach((item) => {
            if (item.labels.section === sectorName && item.labels.row && item.labels.seat && sectorData && sectorData[item.labels.row] && sectorData[item.labels.row][item.labels.seat - 1]) {
                sectorData[item.labels.row][item.labels.seat - 1].endpoint = {...item};
                if (!priceCategories.includes(item.price)) {
                    addPriceCategory(item.price);
                }
            }
        });
    }, [data]);

    return (
        <>
            {children}
        </>
    )
};

const GeneralSeatMap = ({service, toggleEndpoint, localCartState}) => {
    const [priceCategories, setPriceCategories] = React.useState([]);
    const [showSector, setShowSector] = React.useState(null);
    const mapName = service.description ? getTagValue(service.description.split(', '), 'seatmap') : '';
    const seatMapName = mapName || 'defaultMap';
    const seatMap = SEAT_MAPS[seatMapName];
    const {windowDimensions: {windowWidth}} = useWindowDimensions();
    const initialScale = windowWidth < 768 ? 0.4 : 0.6;
    const seatMapClickHandler = (e) => {
        const endpointId = e.target.getAttribute('data-id');
        const sectorClicked = e.target.getAttribute('data-sector');
        if (endpointId) {
            const allowMultipleBuy = e.target.getAttribute('data-multiple');
            toggleEndpoint(endpointId, allowMultipleBuy);
        } else if (sectorClicked) {
            setShowSector(sectorClicked);
        }
    };
    const addPriceCategory = React.useCallback((price) => {
        setPriceCategories((priceCategories) => {
            if (!priceCategories.includes(price)) {
                return [...priceCategories, price].sort((a, b) => b - a);
            }
            return priceCategories;
        });
    }, [setPriceCategories, priceCategories]);
    const showInitialMap = () => {
        setShowSector(null);
    };
    return(
        <div className='seat-map position-relative'>
            <div className='seat-map__main'>
                <div className='padding-top-1rem padding-bottom-1rem mw768-padding-bottom-2rem mw768-padding-top-1dot5rem display-grid gap-1rem-1rem price-grid'>
                    {priceCategories.map((price, index) =>
                        <div key={index} className='display-flex align-items-center'>
                            <div className={`seat seat--price pointer-events-none seat--color-${index}`}></div>
                            <p className='padding-left-0dot5rem text-md color-gray-200 white-space-nowrap'>{price} ₴</p>
                        </div>
                    )}
                </div>
                <div className='seat-map-container padding-top-1dot5rem'>
                    <div className='margin-0-auto position-relative seat-map-container__scaling' onClick={seatMapClickHandler}>
                        <TransformWrapper {...{minScale: 0.2, maxScale: 1, centerZoomedOut: true, centerOnInit: true, initialScale, doubleClick: {disabled: true}}}>
                            <MapActions {...{initialScale}}/>
                            <TransformComponent wrapperClass='seat-map-container__scaling'>
                                <svg  className='display-block margin-left-5rem margin-right-5rem malevich-concert-arena-seats position-relative' width={seatMap.width} height={seatMap.height} fill="none" xmlns="http://www.w3.org/2000/svg">
                                    {!!seatMap.static && seatMap.static}
                                    {
                                        Object.keys(seatMap.seats).map((sectorName, index) =>
                                            <SectorEndpoints key={index} {...{sectorName, serviceId: service.id, sectorData: seatMap.seats[sectorName], addPriceCategory, priceCategories}}>
                                                    {Object.keys(seatMap.seats[sectorName]).map((rowName, rowIndex) =>
                                                        seatMap.seats[sectorName][rowName].map(({x, y, rx, transform, width, height, endpoint, svgTag, d, allowMultipleBuy}, seatIndex) =>
                                                                <Seat key={`${sectorName}-${rowIndex}-${seatIndex}`} {...{endpoint, localCartState, priceCategories, x, y, rx, transform, width, height, svgTag, d, allowMultipleBuy, sectorName, row: rowName, seat: seatIndex + 1}}/>
                                                        )
                                                    )}
                                            </SectorEndpoints>
                                        )
                                    }
                                    {
                                        Object.values(seatMap.clickableSectors).map(({x, y, rx, transform, width, height, svgTag, d, name, classes}) =>
                                            <ClickableSector key={`${name}`} {...{x, y, rx, transform, width, height, svgTag, d, name, classes, serviceId: service.id}}/>
                                        )
                                    }
                                    {!!seatMap.staticTexts && seatMap.staticTexts}
                                </svg>
                            </TransformComponent>
                        </TransformWrapper>
                    </div>
                </div>
            </div>
            {!!showSector &&
                <React.Suspense fallback={<div className='seat-map-container__loading-sector'><div className='seat-map-container__loading-indicator'><LogoLoadingIcon className='display-block width-6rem height-6rem'/></div></div>}>
                    <div className='seat-map__sector position-absolute left-0 top-0 width-100percent height-100percent'>
                        <SectorMap {...{sectorName: showSector}}>
                            {({map}) =>
                                <SectorData {...{serviceId: service.id, map, sectorName: showSector}}>
                                    {({priceCategories}) => 
                                        <SectorSeatMap {...{localCartState, seatMapClickHandler, map, priceCategories, showInitialMap}}/>
                                    }
                                </SectorData>
                            }
                        </SectorMap>
                    </div>
                </React.Suspense>
            }
        </div>
    )
};

export default React.memo(GeneralSeatMap);